import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { QUERY_KEYS } from '~/api/query-client-keys'
import { BackendHTTPError } from '~/api/shared/BackendHTTPError'

// Type alias for image data URLs
export type ImageDataURL = string

// Function to stitch individual images into a single montage
async function stitchImagesIntoMontage(
  imageUrls: string[],
  montageRows: number,
  montageColumns: number,
): Promise<ImageDataURL> {
  return new Promise((resolve, reject) => {
    if (imageUrls.length === 0) {
      reject(new Error('No images to stitch'))
      return
    }

    const loadedImages: HTMLImageElement[] = []
    let loadedCount = 0

    // Load all images first
    imageUrls.forEach((url, index) => {
      const img = new Image()
      img.onload = () => {
        loadedImages[index] = img
        loadedCount++

        // Once all images are loaded, create the stitched montage
        if (loadedCount === imageUrls.length) {
          try {
            // Use the dimensions of the first loaded image
            const siteWidth = loadedImages[0].width
            const siteHeight = loadedImages[0].height

            // Support high-DPI/Retina displays for sharper rendering
            const dpr = window.devicePixelRatio || 1
            // Create canvas for the full montage
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d', {
              // Optimize for quality
              alpha: true,
              willReadFrequently: false,
            })

            if (!ctx) {
              reject(new Error('Could not get canvas context for stitching'))
              return
            }

            // Disable image smoothing to preserve quality and avoid blurring
            ctx.imageSmoothingEnabled = false

            // Set canvas size to accommodate all sites with DPI scaling
            const logicalWidth = montageColumns * siteWidth
            const logicalHeight = montageRows * siteHeight
            canvas.width = logicalWidth * dpr
            canvas.height = logicalHeight * dpr
            canvas.style.width = `${logicalWidth}px`
            canvas.style.height = `${logicalHeight}px`

            // Scale context to match device pixel ratio
            ctx.scale(dpr, dpr)

            // Draw each site image in its correct position
            loadedImages.forEach((img, siteIndex) => {
              if (img) {
                const row = Math.floor(siteIndex / montageColumns)
                const col = siteIndex % montageColumns
                const x = col * siteWidth
                const y = row * siteHeight

                ctx.drawImage(img, x, y, siteWidth, siteHeight)
              }
            })

            // Convert to data URL with WebP format for better compression and quality
            const stitchedImageUrl = canvas.toDataURL('image/webp', 1.0)
            resolve(stitchedImageUrl)
          } catch (error) {
            reject(
              new Error(
                `Failed to stitch images: ${error instanceof Error ? error.message : 'Unknown error'}`,
              ),
            )
          }
        }
      }

      img.onerror = () => {
        reject(new Error(`Failed to load image for stitching: ${url}`))
      }

      img.src = url
    })
  })
}

/**
 * Custom hook to stitch Image data URLS into a single Image
 *
 * @param extractedImages
 * @param montageRows
 * @param montageColumns
 * @param enabled
 */
export function useStitchedMontageImage(
  extractedImages: ImageDataURL[] | undefined,
  montageRows: number,
  montageColumns: number,
  rawUrls: string[],
  enabled: boolean = true,
) {
  // Create a stable cache key from the request urls (without query params)
  const datasetFiles = rawUrls.map(url => {
    const baseUrl = url.split('?')[0]
    const baseUrlParts = baseUrl.split('/')
    const file = baseUrlParts[baseUrlParts.length - 1]
    return file
  })
  const imagesCacheKey = `${datasetFiles.join('-')}-${montageRows}x${montageColumns}`

  return useQuery({
    queryKey: [
      QUERY_KEYS.STITCHED_MONTAGE,
      imagesCacheKey,
      montageRows,
      montageColumns,
    ],
    queryFn: async (): Promise<string> => {
      if (!extractedImages || extractedImages.length === 0) {
        throw new Error('No images to stitch')
      }

      if (extractedImages.length === 1) {
        // For single images, return directly without stitching
        return extractedImages[0]
      }

      return await stitchImagesIntoMontage(extractedImages, montageRows, montageColumns)
    },
    enabled: enabled && !!(extractedImages && extractedImages.length > 0),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

/**
 * Extracts all wells' site images from a sprite image.
 * Used by PlateHero to avoid rendering 96+ wells each with CSS background-position on the same large sprite.
 * Returns data URLs instead of blob URLs to prevent browser refetching when styles are re-applied.
 *
 * @param spriteUrl - URL of the sprite image
 * @param numSitesPerMontage - Number of sites in the montage layout (e.g., 4 for 2x2, 6 for 2x3)
 * @param wellSequence - Array of well names in sprite order (e.g., ["A1", "A2", "B1", ...])
 * @param enabled - Whether to run the extraction
 * @returns Map of well -> array of data URLs (base64 encoded) for that well's sites
 */
export function useExtractedPlateSprite(
  spriteUrl: string | null,
  numSitesPerMontage: number,
  wellSequence: string[] | undefined,
  enabled: boolean = false,
) {
  // Create a stable cache key from the sprite URL and configuration
  // Use the number of wells (not their names) for the cache key
  // This way sprites with the same dimensions share data URLs
  const baseSpriteUrl = spriteUrl?.split('?')[0] // Remove query params for stable key
  const numWells = wellSequence?.length ?? 0

  const cacheKey = useMemo(
    () =>
      baseSpriteUrl && numWells > 0
        ? `${baseSpriteUrl}-${numSitesPerMontage}-${numWells}wells`
        : null,
    [baseSpriteUrl, numSitesPerMontage, numWells],
  )

  const query = useQuery({
    queryKey: [QUERY_KEYS.EXTRACTED_PLATE_SPRITE, cacheKey],
    queryFn: async (): Promise<Record<string, string[]>> => {
      if (!spriteUrl || !wellSequence || wellSequence.length === 0) {
        throw new Error('Sprite URL and well sequence are required')
      }

      const spriteNumWells = wellSequence.length

      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'

        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d', { alpha: false })

            if (!ctx) {
              reject(new Error('Could not get canvas context'))
              return
            }

            // Calculate dimensions for each site image
            const siteWidth = Math.floor(img.width / numSitesPerMontage)
            const siteHeight = Math.floor(img.height / spriteNumWells)

            const wellToSiteUrls: Record<string, string[]> = {}

            // Extract all wells and their sites (synchronous with data URLs)
            wellSequence.forEach((well, spriteWellIndex) => {
              const siteUrls: string[] = []

              for (let siteIndex = 0; siteIndex < numSitesPerMontage; siteIndex++) {
                canvas.width = siteWidth
                canvas.height = siteHeight

                // Calculate source position using the same formula as CSS background-position
                const sourceX =
                  numSitesPerMontage === 1
                    ? 0
                    : Math.floor(
                        (siteIndex / (numSitesPerMontage - 1)) *
                          (img.width - siteWidth),
                      )
                const sourceY =
                  spriteNumWells === 1
                    ? 0
                    : Math.floor(
                        (spriteWellIndex / (spriteNumWells - 1)) *
                          (img.height - siteHeight),
                      )

                // Draw the specific portion of the sprite
                ctx.drawImage(
                  img,
                  sourceX,
                  sourceY,
                  siteWidth,
                  siteHeight,
                  0,
                  0,
                  siteWidth,
                  siteHeight,
                )

                // Convert to data URL (synchronous operation)
                const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
                siteUrls[siteIndex] = dataUrl
              }

              wellToSiteUrls[well] = siteUrls
            })

            // All extraction complete, resolve with the data URLs
            resolve(wellToSiteUrls)
          } catch (error) {
            reject(
              new Error(
                `Failed to extract plate sprite images: ${error instanceof Error ? error.message : 'Unknown error'}`,
              ),
            )
          }
        }

        img.onerror = () => {
          reject(new Error(`Failed to load sprite image: ${spriteUrl}`))
        }

        img.src = spriteUrl
      })
    },
    enabled: enabled && !!(spriteUrl && wellSequence && wellSequence.length > 0),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  return query
}

/**
 * Returns an array of blob: URLs for the provided image URLs by fetching them as Blobs.
 * Using blob URLs avoids canvas tainting when we later stitch images together.
 */
export function useLargeImageBlobUrls(
  imageUrls: string[] | undefined,
  enabled: boolean = false,
) {
  // Create stable cache key from base URLs (without query params) to maintain cache across presigned URL rotations
  const baseUrls =
    imageUrls?.map(url => {
      const baseUrl = url.split('?')[0]
      const parts = baseUrl.split('/')
      return parts[parts.length - 1]
    }) || []
  const key = baseUrls.length > 0 ? baseUrls.join('|') : null

  return useQuery({
    queryKey: [QUERY_KEYS.BLOB_IMAGES, key],
    queryFn: async (): Promise<string[]> => {
      if (!imageUrls || imageUrls.length === 0) {
        throw new Error('No image URLs provided')
      }

      // Fetch images in parallel while preserving order via Promise.all
      const blobUrls = await Promise.all(
        imageUrls.map(async src => {
          // Use force-cache to leverage browser HTTP cache from preloading.
          // This enables the preloading optimization in usePreloadAdjacentWells:
          // - Preloading fetches images with force-cache, warming the browser cache
          // - When user navigates, this fetch hits the cache instead of network
          // - Results in ~1-2ms loads vs ~1000ms network fetches
          //
          // Cache behavior with presigned URLs:
          // - warmImageURL() reuses the same presigned URL for the same image (until near expiration)
          // - This ensures preload and render use identical URLs, so caching works correctly
          // - If presigned URL expires, S3 returns 403, which is caught below and re-thrown
          // - React Query will retry failed requests, giving the app a chance to get a fresh URL
          const response = await fetch(src, { cache: 'force-cache' })

          if (response.status === 404) {
            throw new BackendHTTPError('404', response.status, '')
          }
          if (!response.ok) {
            // Preserve actual HTTP status (e.g., 403 for expired presigned URLs)
            throw new BackendHTTPError(
              `Fetching image failed with status ${response.status}`,
              response.status,
              String(response.status),
            )
          }

          const blob = await response.blob()
          return URL.createObjectURL(blob)
        }),
      )

      return blobUrls
    },
    enabled: enabled && !!(imageUrls && imageUrls.length > 0),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}
